'use client';

import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';
import { cn } from '@/lib/utils';
import { Check, Loader2 } from 'lucide-react';
import { useState, useTransition, useEffect } from 'react';
import { createCheckoutSession } from '@/lib/actions/stripe';
import type { PlanName } from '@/lib/stripe';
import { toast } from '@/components/toast';
import { FreePlanCard } from '../../components/free-plan-card';
import Image from 'next/image';

interface Plan {
  name: PlanName | 'Enterprise';
  monthlyPrice: number | null;
  annualPrice: number | null;
  records: string;
  features: string[];
  buttonText: string;
  buttonVariant: 'default' | 'secondary' | 'outline';
  popular: boolean;
  isEnterprise?: boolean;
}

interface PricingGridProps {
  currentPlan?: string | null;
  currentBillingInterval?: string | null;
}

export const PricingGrid = ({
  currentPlan,
  currentBillingInterval,
}: PricingGridProps) => {
  // Initialize toggle based on user's actual billing interval
  const [isAnnual, setIsAnnual] = useState(currentBillingInterval === 'annual');
  const [isPending, startTransition] = useTransition();
  const [loadingPlan, setLoadingPlan] = useState<string | null>(null);

  // Update toggle when billing interval changes (e.g., after subscription update)
  useEffect(() => {
    if (currentBillingInterval) {
      const shouldBeAnnual = currentBillingInterval === 'annual';
      console.log(
        'Setting toggle based on current billing:',
        currentBillingInterval,
        '-> Annual:',
        shouldBeAnnual,
      );
      setIsAnnual(shouldBeAnnual);
    }
  }, [currentBillingInterval]);

  // Handle return from Stripe checkout or subscription updates
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const success = urlParams.get('success');
    const canceled = urlParams.get('canceled');
    const updated = urlParams.get('updated');
    const error = urlParams.get('error');

    if (success || canceled || updated || error) {
      // Clear URL parameters without navigation
      const newUrl = window.location.pathname;
      window.history.replaceState({}, '', newUrl);

      if (success === 'true') {
        // Payment successful - server already revalidated data
        toast({
          type: 'success',
          description:
            'Payment successful! Your subscription has been activated.',
        });
      } else if (canceled === 'true') {
        // Payment canceled/failed - show error
        toast({
          type: 'error',
          description: 'Payment was canceled or failed. Please try again.',
        });
      } else if (updated === 'true') {
        // Subscription updated successfully
        toast({
          type: 'success',
          description: 'Your subscription has been updated successfully!',
        });
      } else if (error) {
        // Error during processing
        toast({
          type: 'error',
          description:
            'There was an issue processing your request. Please try again.',
        });
      }
    }
  }, []);

  const plans: Plan[] = [
    {
      name: 'starter',
      monthlyPrice: 19,
      annualPrice: 17,
      records: 'Up to 1K records/month',
      features: ['AI Chatbot Access', 'Basic API Access', 'Email Support'],
      buttonText: 'Upgrade',
      buttonVariant: 'default',
      popular: false,
    },
    {
      name: 'pro',
      monthlyPrice: 59,
      annualPrice: 50,
      records: 'Up to 10K records/month',
      features: [
        'Everything in Starter',
        'CRM Sync',
        'Advanced API Access',
        'Priority Support',
      ],
      buttonText: 'Upgrade',
      buttonVariant: 'default',
      popular: true,
    },
    {
      name: 'business',
      monthlyPrice: 149,
      annualPrice: 127,
      records: 'Up to 50K records/month',
      features: ['Everything in Pro', 'Team Management', 'Premium Support'],
      buttonText: 'Upgrade',
      buttonVariant: 'default',
      popular: false,
    },
    {
      name: 'Enterprise',
      monthlyPrice: null,
      annualPrice: null,
      records: '100K+ records/month',
      features: [
        'All Business Features',
        'Custom Integrations',
        'Dedicated Support',
      ],
      buttonText: 'Contact Sales',
      buttonVariant: 'default',
      popular: false,
      isEnterprise: true,
    },
  ];

  const getButtonText = (plan: Plan) => {
    // Check both plan name AND billing interval for current plan
    const isCurrentPlanAndInterval =
      plan.name === currentPlan &&
      !plan.isEnterprise &&
      ((isAnnual && currentBillingInterval === 'annual') ||
        (!isAnnual && currentBillingInterval === 'monthly'));

    if (isCurrentPlanAndInterval) return 'Current Plan';

    // Define plan hierarchy: starter < pro < business
    const planHierarchy = { starter: 1, pro: 2, business: 3 };
    const currentPlanLevel =
      planHierarchy[currentPlan as keyof typeof planHierarchy] || 0;
    const targetPlanLevel =
      planHierarchy[plan.name as keyof typeof planHierarchy] || 0;

    if (currentPlan && !plan.isEnterprise) {
      if (targetPlanLevel > currentPlanLevel) return 'Upgrade';
      if (targetPlanLevel < currentPlanLevel) return 'Downgrade';
    }

    return plan.buttonText;
  };

  const handleSubscribe = async (plan: Plan) => {
    // Check if this is the exact same plan and billing interval
    const isSamePlanAndInterval =
      plan.name === currentPlan &&
      ((isAnnual && currentBillingInterval === 'annual') ||
        (!isAnnual && currentBillingInterval === 'monthly'));

    if (plan.isEnterprise || isSamePlanAndInterval || loadingPlan) return;

    setLoadingPlan(plan.name);
    startTransition(async () => {
      try {
        // Always go through Stripe checkout for payment
        // Plan will be updated after successful payment via webhook
        await createCheckoutSession({
          planName: plan.name as PlanName,
          billingInterval: isAnnual ? 'annual' : 'monthly',
        });
      } catch (error) {
        // Check if this is a redirect error from Next.js (which is expected for unauthenticated users)
        if (
          error &&
          typeof error === 'object' &&
          'digest' in error &&
          typeof error.digest === 'string' &&
          error.digest.includes('NEXT_REDIRECT')
        ) {
          // This is a redirect to login page, don't show error toast
          console.log('Redirecting to login for authentication');
          return;
        }

        console.error('Failed to process subscription:', error);
        toast({
          type: 'error',
          description: 'Failed to process subscription. Please try again.',
        });
      } finally {
        setLoadingPlan(null);
      }
    });
  };

  return (
    <section className="w-full px-4 sm:px-6">
      <div className="max-w-[1232px] mx-auto pt-11">
        {/* Billing Toggle */}
        <div className="flex flex-wrap items-center justify-center gap-3 mb-[42px]">
          <span
            className={cn(
              'font-dmSans text-sm sm:text-base',
              !isAnnual ? 'text-gray-900 font-semibold' : 'text-gray-600',
            )}
          >
            Monthly
          </span>
          <Switch
            checked={isAnnual}
            onCheckedChange={setIsAnnual}
            className={cn()}
          />
          <span
            className={cn(
              'font-dmSans text-sm sm:text-base',
              isAnnual ? 'text-gray-900 font-semibold' : 'text-gray-600',
            )}
          >
            Annual
          </span>

          <span className="inline-block mt-2 sm:mt-0 px-3 py-1 rounded-full bg-[linear-gradient(90deg,#0A8FFB_0%,#AC87EB_75%)] text-white text-xs font-medium font-dmSans">
            15% Discount
          </span>

          {/* Show current billing status */}
          {currentPlan && currentBillingInterval && (
            <div className="w-full flex justify-center mt-2">
              <span className="text-xs text-gray-500 font-dmSans">
                Your current plan:{' '}
                {currentPlan.charAt(0).toUpperCase() + currentPlan.slice(1)} (
                {currentBillingInterval})
              </span>
            </div>
          )}
        </div>

        <FreePlanCard />

        {/* Pricing Cards */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-[22px]">
          {plans.map((plan) => {
            // Check both plan name AND billing interval for current plan
            const isCurrent =
              plan.name === currentPlan &&
              !plan.isEnterprise &&
              ((isAnnual && currentBillingInterval === 'annual') ||
                (!isAnnual && currentBillingInterval === 'monthly'));
            const isPlanLoading = loadingPlan === plan.name;

            return (
              <div
                key={plan.name}
                className={cn(
                  'relative h-min',
                  plan.popular &&
                    'p-[3px] bg-primary-gradient h-min rounded-[22px] ',
                )}
              >
                {plan.popular && (
                  <Image
                    src="/images/popular-plan.png"
                    alt="Plan Background"
                    width={5000}
                    height={200}
                    className="top-1/2 hidden lg:flex left-1/2 absolute -translate-x-1/2 -translate-y-1/2 min-h-[732px] min-w-[629px]"
                  />
                )}

                <div
                  className={cn(
                    'relative flex z-10 flex-col h-min justify-between rounded-[20px] transition-shadow duration-200',
                    isCurrent
                      ? 'bg-[linear-gradient(163.85deg,#2C9FFF_0.78%,#C46BFF_100%)] text-white'
                      : 'bg-white border border-[#c9c9c9]',

                    plan.popular && 'bg-white',
                  )}
                >
                  {plan.popular && (
                    <div className="absolute top-4 right-4 flex justify-center items-center px-2 bg-white rounded-[5px] h-6 text-xs bg-primary-gradient">
                      Most Popular
                    </div>
                  )}

                  <div className="px-5 py-8 sm:px-[35px] sm:py-10">
                    {/* Plan Name */}
                    <h3
                      className={cn(
                        'text-xl sm:text-2xl leading-none font-dmSans mb-2.5',
                        isCurrent ? 'text-white' : 'gradient-text',
                      )}
                    >
                      {plan.name === 'Enterprise'
                        ? plan.name
                        : plan.name.charAt(0).toUpperCase() +
                          plan.name.slice(1)}
                    </h3>

                    {/* Price or Contact Us */}
                    {plan.isEnterprise ? (
                      <div
                        className={cn(
                          'text-[24px] sm:text-[32px] leading-none font-dmSans font-medium',
                          isCurrent ? 'text-white' : 'text-black',
                        )}
                      >
                        Contact Us
                      </div>
                    ) : (
                      <div>
                        <div className="flex items-baseline">
                          <span
                            className={cn(
                              'text-[24px] sm:text-[32px] leading-none font-dmSans font-medium',
                              isCurrent ? 'text-white' : 'text-black',
                            )}
                          >
                            ${isAnnual ? plan.annualPrice : plan.monthlyPrice}
                          </span>
                          <span
                            className={cn(
                              'text-[18px] sm:text-[22px] leading-none font-dmSans',
                              isCurrent ? 'text-white' : 'text-[#8E8E8E]',
                            )}
                          >
                            /month
                          </span>
                        </div>
                      </div>
                    )}

                    {/* Records */}
                    <p
                      className={cn(
                        'text-sm sm:text-base leading-none font-dmSans mt-1 mb-10',
                        isCurrent ? 'text-white' : 'text-[#404040]',
                      )}
                    >
                      {plan.records}
                    </p>

                    {/* Features */}
                    <ul className="space-y-3 mb-6">
                      {plan.features.map((feature, i) => (
                        <li key={i} className="flex items-center">
                          <Check
                            className={cn(
                              'size-5 shrink-0',
                              isCurrent ? 'text-white' : 'text-purple-600',
                            )}
                          />
                          <span
                            className={cn(
                              'ml-3 text-sm sm:text-base',
                              isCurrent ? 'text-white' : 'text-gray-700',
                            )}
                          >
                            {feature}
                          </span>
                        </li>
                      ))}
                    </ul>
                  </div>

                  {/* Action Button */}
                  <div className="w-full px-[15px] pb-[35px]">
                    <Button
                      variant={isCurrent ? 'default' : 'gradient'}
                      className={cn(
                        'w-full h-[50px] sm:h-[60px] text-[18px] sm:text-[22px] leading-none rounded-full',
                        isCurrent ? '' : '',
                      )}
                      onClick={() => handleSubscribe(plan)}
                      disabled={isPlanLoading || isCurrent}
                    >
                      {isPlanLoading ? (
                        <Loader2 className="size-4 animate-spin" />
                      ) : (
                        getButtonText(plan)
                      )}
                    </Button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
};
